import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Cloud, Link, ExternalLink, X, Loader, Check, AlertCircle, Calendar, ChevronDown } from 'lucide-react';
import { useApp } from '../AppProvider';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { translations } from '../../lib/i18n/translations';
import { NotionToggle } from '../ui/notion';
import { GoogleConnectModal, GoogleScopeSelection } from './GoogleConnectModal';
import { mancaPermessoCalendario as calcolaMancaPermessoCalendario } from '../../lib/google/calendarScope';
import { usePermissions } from '../../lib/hooks/usePermissions';

interface GoogleAccountSectionProps {
  userId: string;
}

interface GoogleAccountInfo {
  connected: boolean;
  email: string | null;
  user_id: string | null;
  refresh_token: string | null;
  calendar_ids: string[];
}

interface GoogleCalendar {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
}

export function GoogleAccountSection({ userId }: GoogleAccountSectionProps) {
  const [loading, setLoading] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [accountInfo, setAccountInfo] = useState<GoogleAccountInfo>({
    connected: false,
    email: null,
    user_id: null,
    refresh_token: null,
    calendar_ids: []
  });
  const [error, setError] = useState<string | null>(null);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const { settings } = useSettingsStore();
  const { toast } = useApp();
  // Collegare Google: solo chi ha il permesso "Collega Google o Outlook" (calendar.sync)
  const { hasPermission } = usePermissions();
  const canSyncCalendar = hasPermission('calendar', 'sync');

  // Activities sync settings
  const [syncActivities, setSyncActivities] = useState(false);
  const [syncActivitiesCalendarId, setSyncActivitiesCalendarId] = useState('');
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  // Account collegato ma senza il permesso Calendario: l'elenco dei calendari resta
  // vuoto e prima non lo diceva nessuno (Mattia Giardini, Viva la Farina, 18/09/2026).
  const [mancaPermessoCalendario, setMancaPermessoCalendario] = useState(false);
  const [fetchingCalendars, setFetchingCalendars] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchGoogleAccount();
  }, [userId]);

  // Check if Google Workspace integration is enabled for this organization
  useEffect(() => {
    if (settings) {
      setGoogleEnabled(settings.google_workspace_enabled || false);
    }
  }, [settings]);

  // Auto-fetch calendars when account is connected
  useEffect(() => {
    if (accountInfo.connected && googleCalendars.length === 0 && !fetchingCalendars) {
      fetchGoogleCalendars();
    }
  }, [accountInfo.connected]);

  const fetchGoogleAccount = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('google_account_connected, google_account_email, google_user_id, google_refresh_token, google_calendar_ids, google_workspace_scopes, sync_activities, sync_activities_calendar_id')
        .eq('id', userId)
        .single();

      if (error) {
        throw error;
      }

      const hasTokenAndId = !!(data.google_refresh_token && data.google_user_id);

      setAccountInfo({
        connected: data.google_account_connected && hasTokenAndId,
        email: data.google_account_email,
        user_id: data.google_user_id,
        refresh_token: data.google_refresh_token,
        calendar_ids: data.google_calendar_ids || []
      });

      // Set activities sync settings
      setMancaPermessoCalendario(
        calcolaMancaPermessoCalendario(!!data.google_account_connected, data.google_workspace_scopes),
      );
      setSyncActivities(data.sync_activities || false);
      setSyncActivitiesCalendarId(data.sync_activities_calendar_id || '');
    } catch (error) {
      console.error('Error fetching Google account info:', error);
      setError('Failed to load Google account information');
    } finally {
      setLoading(false);
    }
  };

  // Apre il modale di selezione scope
  const handleConnectGoogle = () => {
    if (!settings?.google_workspace_client_id) {
      setError('Google Client ID is not configured in organization settings');
      toast.error('Google Workspace non è configurato. Contatta l\'amministratore.');
      return;
    }
    setConnectModalOpen(true);
  };

  // Lancia OAuth con gli scope selezionati nel modale
  const handleConfirmScopes = async (selection: GoogleScopeSelection) => {
    try {
      setInitializing(true);
      setError(null);

      const clientId = settings?.google_workspace_client_id;
      if (!clientId) {
        throw new Error('Google Client ID is not configured');
      }

      // Persisti le scelte sync_* nel DB settings (best-effort, non blocca il flusso)
      try {
        await supabase.from('settings').update({
          sync_calendar: selection.calendar,
          sync_drive: selection.drive,
          sync_contacts: selection.contacts,
          sync_gmail: selection.gmail,
        }).eq('organization_id', settings.organization_id);
      } catch (e) {
        console.warn('[GoogleConnect] Failed to persist scope selection, continuing OAuth:', e);
      }

      // Costruisci scope dinamici in base alla selezione
      const scopes = [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ];
      if (selection.calendar) scopes.push('https://www.googleapis.com/auth/calendar');
      if (selection.drive) scopes.push('https://www.googleapis.com/auth/drive.file');
      if (selection.contacts) scopes.push('https://www.googleapis.com/auth/contacts.readonly');
      if (selection.gmail) {
        scopes.push(
          'https://www.googleapis.com/auth/gmail.readonly',
          'https://www.googleapis.com/auth/gmail.send',
          'https://www.googleapis.com/auth/gmail.compose'
        );
      }
      if (selection.meet) {
        scopes.push('https://www.googleapis.com/auth/meetings.space.readonly');
      }
      if (selection.ads) {
        scopes.push('https://www.googleapis.com/auth/adwords');
      }

      const state = encodeURIComponent(JSON.stringify({ redirectTo: '/' }));
      const redirectUri = 'https://app.flusia.it/digital-google-connect';

      const oauthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      oauthUrl.searchParams.append('client_id', clientId);
      oauthUrl.searchParams.append('redirect_uri', redirectUri);
      oauthUrl.searchParams.append('response_type', 'code');
      oauthUrl.searchParams.append('scope', scopes.join(' '));
      oauthUrl.searchParams.append('access_type', 'offline');
      oauthUrl.searchParams.append('prompt', 'consent');
      oauthUrl.searchParams.append('state', state);

      if (settings.google_workspace_domain) {
        oauthUrl.searchParams.append('hd', settings.google_workspace_domain);
      }

      window.location.href = oauthUrl.toString();
    } catch (error) {
      console.error('Error initializing Google authorization flow:', error);
      setError('Failed to initialize Google authorization');
      toast.error('Non è stato possibile avviare il processo di autorizzazione Google');
      setInitializing(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Sei sicuro di voler disconnettere il tuo account Google? Tutte le integrazioni cesseranno di funzionare.')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      const { error } = await supabase
        .from('user_profiles')
        .update({
          google_account_connected: false,
          google_refresh_token: null,
          google_account_email: null,
          google_user_id: null,
          google_calendar_ids: [],
          google_workspace_scopes: null,
          sync_activities: false,
          sync_activities_calendar_id: null
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      setAccountInfo({
        connected: false,
        email: null,
        user_id: null,
        refresh_token: null,
        calendar_ids: []
      });
      setSyncActivities(false);
      setSyncActivitiesCalendarId('');
      setGoogleCalendars([]);

      toast.success('Account Google disconnesso con successo');
    } catch (error) {
      console.error('Error disconnecting Google account:', error);
      setError('Failed to disconnect Google account');
      toast.error('Non è stato possibile disconnettere l\'account Google');
    } finally {
      setDisconnecting(false);
    }
  };

  // Fetch Google Calendars
  const fetchGoogleCalendars = async () => {
    if (!accountInfo.connected) return;
    if (mancaPermessoCalendario) {
      setGoogleCalendars([]);
      return;
    }

    try {
      setFetchingCalendars(true);
      setError(null);

      const organizationId = settings?.organization_id;
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('google-calendar-list', {
        body: {
          organization_id: organizationId,
          user_id: user.id
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch Google calendars');
      }

      if (data && Array.isArray(data.calendars)) {
        setGoogleCalendars(data.calendars);
      } else {
        setGoogleCalendars([]);
      }
    } catch (error) {
      console.error('Error fetching Google Calendars:', error);
      // Prima qui c'era un "silent fail": l'elenco restava vuoto senza una parola, e chi
      // aveva collegato Google senza il permesso Calendario non capiva perche'.
      const risposta = (error as { context?: Response })?.context;
      let motivo = '';
      try {
        motivo = risposta ? String(((await risposta.clone().json()) as { error?: string })?.error || '') : '';
      } catch {
        /* corpo non leggibile: si ricade sul messaggio generico */
      }
      if (/calendar/i.test(motivo)) {
        setMancaPermessoCalendario(true);
      } else {
        setError('Non è stato possibile leggere i calendari di Google. Riprova fra poco.');
      }
      setGoogleCalendars([]);
    } finally {
      setFetchingCalendars(false);
    }
  };

  // Save Activities Sync Settings
  const handleSaveActivitiesSettings = async () => {
    if (!userId) return;

    try {
      setSavingSettings(true);
      setError(null);

      const { error } = await supabase
        .from('user_profiles')
        .update({
          sync_activities: syncActivities,
          sync_activities_calendar_id: syncActivitiesCalendarId || null
        })
        .eq('id', userId);

      if (error) throw error;

      toast.success('Impostazioni salvate');
    } catch (error) {
      console.error('Error saving activities settings:', error);
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle toggle change with auto-save
  const handleSyncToggle = async (enabled: boolean) => {
    setSyncActivities(enabled);

    // Auto-save when disabling
    if (!enabled && userId) {
      try {
        await supabase
          .from('user_profiles')
          .update({
            sync_activities: false
          })
          .eq('id', userId);
      } catch (error) {
        console.error('Error auto-saving sync setting:', error);
      }
    }
  };

  const { settings: settingsStore } = useSettingsStore();
  const t = translations[settingsStore?.language || 'en'].settings.googleWorkspace.userConnection;

  // Get enabled features list
  const enabledFeatures = [];
  if (settings?.sync_calendar) enabledFeatures.push('Calendario');
  if (settings?.sync_drive) enabledFeatures.push('Drive');
  if (settings?.sync_contacts) enabledFeatures.push('Contatti');

  if (!googleEnabled) {
    return (
      <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-4 bg-[#FBFBFA] dark:bg-gray-800/30">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[#787774] dark:text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-[#37352F] dark:text-white">
              Google Workspace non abilitato
            </h3>
            <p className="mt-1 text-xs text-[#787774] dark:text-gray-400">
              L'integrazione con Google Workspace non è attiva per la tua organizzazione.
              Contatta l'amministratore se desideri utilizzare questa funzionalità.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <Loader className="h-5 w-5 animate-spin text-[#787774]" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 p-4 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Si è verificato un errore
              </h3>
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                {error}. Ricarica la pagina o contatta l'assistenza.
              </p>
            </div>
          </div>
        </div>
      ) : accountInfo.connected ? (
        <>
          {/* Connected Account Card */}
          <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-4 bg-white dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#37352F] dark:text-white">
                    Account collegato
                  </p>
                  <p className="text-xs text-[#787774] dark:text-gray-400">
                    {accountInfo.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setConnectModalOpen(true)}
                  disabled={initializing}
                  className="px-3 py-1.5 text-xs font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded transition-colors"
                  title="Modifica i permessi concessi a Flusia"
                >
                  Modifica permessi
                </button>
                <button
                  onClick={handleDisconnectGoogle}
                  disabled={disconnecting}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex items-center gap-1"
                >
                  {disconnecting ? (
                    <Loader className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                  Disconnetti
                </button>
              </div>
            </div>
          </div>

          {/* Activities Sync Section */}
          <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-4 bg-white dark:bg-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                <span className="text-sm font-medium text-[#37352F] dark:text-white">
                  Sincronizza Attività
                </span>
              </div>
              <NotionToggle
                enabled={syncActivities}
                onClick={() => handleSyncToggle(!syncActivities)}
              />
            </div>

            <p className="text-xs text-[#787774] dark:text-gray-400 mb-4">
              Quando crei un'attività (appuntamento, chiamata, follow-up, videochiamata) nel CRM,
              verrà automaticamente aggiunta al tuo calendario Google come evento.
              Per le videochiamate verrà incluso anche il link di Google Meet.
            </p>

            {mancaPermessoCalendario && canSyncCalendar && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 p-3">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800 dark:text-amber-300">
                    <p className="font-medium">Il collegamento a Google non comprende il calendario</p>
                    <p className="mt-1">
                      Per questo l'elenco dei calendari resta vuoto e le attività non finiscono su Google.
                      Ricollega l'account e spunta <strong>Calendario</strong> fra i permessi.
                    </p>
                    <button
                      onClick={handleConnectGoogle}
                      disabled={initializing}
                      className="mt-2 px-2.5 py-1 rounded border border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
                    >
                      Ricollega con il calendario
                    </button>
                  </div>
                </div>
              </div>
            )}

            {syncActivities && (
              <div className="space-y-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                <div>
                  <label className="block text-xs text-[#787774] dark:text-gray-400 mb-1.5">
                    Calendario di destinazione
                  </label>
                  <div className="relative">
                    <select
                      value={syncActivitiesCalendarId}
                      onChange={(e) => setSyncActivitiesCalendarId(e.target.value)}
                      disabled={fetchingCalendars}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded-md text-[#37352F] dark:text-white focus:outline-none focus:border-[#2383E2] appearance-none pr-8"
                    >
                      <option value="">
                        {fetchingCalendars
                          ? 'Caricamento calendari...'
                          : mancaPermessoCalendario
                            ? 'Nessun calendario: manca il permesso'
                            : 'Seleziona un calendario'}
                      </option>
                      {googleCalendars.map(calendar => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.summary}
                          {calendar.primary && ' (Principale)'}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787774] pointer-events-none" />
                  </div>
                  {fetchingCalendars && (
                    <p className="text-xs text-[#787774] mt-1 flex items-center gap-1">
                      <Loader className="h-3 w-3 animate-spin" />
                      Caricamento calendari in corso...
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSaveActivitiesSettings}
                  disabled={savingSettings || !syncActivitiesCalendarId}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-[#37352F] dark:bg-gray-700 rounded-md hover:bg-[#37352F]/80 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {savingSettings ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Salva impostazioni
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Enabled Features Info */}
          {enabledFeatures.length > 0 && (
            <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-4 bg-[#FBFBFA] dark:bg-gray-800/30">
              <p className="text-xs text-[#787774] dark:text-gray-400">
                <span className="font-medium">Funzionalità abilitate:</span>{' '}
                {enabledFeatures.join(' • ')}
              </p>
            </div>
          )}
        </>
      ) : !canSyncCalendar ? (
        <p className="text-xs text-[#787774] dark:text-gray-400" data-testid="google-connect-not-allowed">
          Il collegamento con Google non è previsto per il tuo ruolo.
        </p>
      ) : (
        /* Not Connected State */
        <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-6 bg-white dark:bg-gray-800/50">
          <div className="flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-[#FBFBFA] dark:bg-gray-800 flex items-center justify-center mb-4">
              <Cloud className="h-6 w-6 text-[#787774] dark:text-gray-400" />
            </div>

            <h3 className="text-sm font-medium text-[#37352F] dark:text-white mb-1">
              Collega il tuo account Google
            </h3>
            <p className="text-xs text-[#787774] dark:text-gray-400 mb-4 max-w-xs">
              Sincronizza calendario, contatti e file per una gestione più efficiente del tuo lavoro
            </p>

            <button
              onClick={handleConnectGoogle}
              disabled={initializing}
              className="inline-flex items-center px-4 py-2 bg-[#2383E2] text-white rounded-md text-sm font-medium hover:bg-[#1a6bc2] focus:outline-none focus:ring-2 focus:ring-[#2383E2] focus:ring-offset-2 disabled:opacity-50 transition-colors"
            >
              {initializing ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Connessione...
                </>
              ) : (
                <>
                  <Link className="h-4 w-4 mr-2" />
                  Connetti Google
                </>
              )}
            </button>

            <p className="text-xs text-[#787774] dark:text-gray-400 mt-3 flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              Verrai reindirizzato a Google per l'autorizzazione
            </p>
          </div>
        </div>
      )}

      {/* Modale selezione scope OAuth */}
      <GoogleConnectModal
        isOpen={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onConfirm={handleConfirmScopes}
        alreadyConnected={accountInfo.connected}
        initialSelection={{
          calendar: settings?.sync_calendar ?? true,
          drive: settings?.sync_drive ?? true,
          contacts: settings?.sync_contacts ?? true,
          gmail: settings?.sync_gmail ?? false,
          meet: false,
          ads: false,
        }}
      />
    </div>
  );
}
